import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Modal } from "react-bootstrap";

export default function AddExistingFiles(props) {
	const [showExistingFilesModel, setShowExistingFilesModel] = useState(false);
	const [courses, setCourses] = useState([]);
	const [enablecheckeditems, setEnableCheckeditems] = useState([]);
	const [enablesectioncheckeditems, setEnableSectionCheckeditems] = useState(
		[]
	);
	const [sections, setSections] = useState([]);

	// Update courses when props change
	useEffect(() => {
		if (props.courses) {
			setCourses(props.courses);
		}
	}, [props.courses]);

	// Extract unique sections from courses
	useEffect(() => {
		const extractSections = () => {
			const tempSections = [];
			if (courses.length > 0) {
				courses.forEach((course) => {
					course.textlist.forEach((value) => {
						if (!tempSections.includes(value.section) && value.file !== "") {
							tempSections.push(value.section);
						}
					});
				});
			}
			return tempSections;
		};

		const uniqueSections = extractSections();
		setSections(uniqueSections);
	}, [courses]);

	// Memoized function to get selected files
	const getSelectedFiles = useCallback(() => {
		const finalCourses = [];
		const selectedNames = new Set(enablecheckeditems);

		courses.forEach((course) => {
			course.textlist.forEach((item) => {
				if (selectedNames.has(item.name)) {
					// Check for duplicates
					const isDuplicate = finalCourses.some(
						(course) => course.name === item.name
					);
					if (!isDuplicate) {
						finalCourses.push(item);
					}
				}
			});
		});

		return finalCourses;
	}, [courses, enablecheckeditems]);

	// Handle adding selected files
	const handlePushItemIntoTextlistArray = useCallback(() => {
		const selectedFiles = getSelectedFiles();
		console.log("Selected files:", selectedFiles);
		props.addFiles(selectedFiles);
		setShowExistingFilesModel(false);
	}, [getSelectedFiles, props]);

	// Handle section checkbox changes
	const handleEnableSectionCheckbox = useCallback(
		(event) => {
			const { checked, value } = event.currentTarget;

			setEnableSectionCheckeditems((prev) =>
				checked ? [...prev, value] : prev.filter((val) => val !== value)
			);

			// Update individual checkboxes based on section selection
			const sectionItems = [];
			courses.forEach((course) => {
				course.textlist.forEach((item) => {
					if (item.section === value) {
						sectionItems.push(item.name);
					}
				});
			});

			setEnableCheckeditems((prev) => {
				if (checked) {
					// Add all items from this section
					const newItems = [...prev];
					sectionItems.forEach((itemName) => {
						if (!newItems.includes(itemName)) {
							newItems.push(itemName);
						}
					});
					return newItems;
				} else {
					// Remove all items from this section
					return prev.filter((val) => !sectionItems.includes(val));
				}
			});
		},
		[courses]
	);

	// Handle individual checkbox changes
	const handleEnableCheckbox = useCallback(
		(event) => {
			const { checked, value } = event.currentTarget;

			setEnableCheckeditems((prev) =>
				checked ? [...prev, value] : prev.filter((val) => val !== value)
			);

			// If unchecking, also uncheck the section if no other items from that section are selected
			if (!checked) {
				let itemSection = null;
				courses.forEach((course) => {
					course.textlist.forEach((item) => {
						if (item.name === value) {
							itemSection = item.section;
						}
					});
				});

				if (itemSection) {
					const otherItemsInSection = [];
					courses.forEach((course) => {
						course.textlist.forEach((item) => {
							if (item.section === itemSection && item.name !== value) {
								otherItemsInSection.push(item.name);
							}
						});
					});

					const hasOtherSelectedItems = otherItemsInSection.some((itemName) =>
						enablecheckeditems.includes(itemName)
					);

					if (!hasOtherSelectedItems) {
						setEnableSectionCheckeditems((prev) =>
							prev.filter((val) => val !== itemSection)
						);
					}
				}
			}
		},
		[courses, enablecheckeditems]
	);

	// Memoized sections with non-empty content
	const nonEmptySections = useMemo(() => {
		return sections.filter((section) => section !== "");
	}, [sections]);

	// Memoized empty sections
	const emptySections = useMemo(() => {
		return sections.filter((section) => section === "");
	}, [sections]);

	// Close modal and reset state
	const handleCloseModal = useCallback(() => {
		setShowExistingFilesModel(false);
		setEnableCheckeditems([]);
		setEnableSectionCheckeditems([]);
	}, []);

	// Check if section is selected
	const isSectionSelected = useCallback(
		(section) => {
			return enablesectioncheckeditems.includes(section);
		},
		[enablesectioncheckeditems]
	);

	// Check if item is selected
	const isItemSelected = useCallback(
		(itemName) => {
			return enablecheckeditems.includes(itemName);
		},
		[enablecheckeditems]
	);

	// Handle PDF view
	const handleViewPdf = useCallback(
		(file) => {
			props.showPdf(file);
		},
		[props]
	);

	return (
		<>
			<Modal
				show={showExistingFilesModel}
				onHide={handleCloseModal}
				dialogClassName="modal-sm-30px"
				size="md"
				backdropClassName="childmodal"
				centered>
				<Modal.Body style={{ padding: "0px 12px 0px 12px" }}>
					<div className="row" style={{ background: "#F4F7F9" }}>
						<div className="col-11">
							<h2
								style={{
									fontSize: "18px",
									fontWeight: "500",
									padding: "15px",
								}}>
								Add Existing Course Materials
							</h2>
						</div>
						<div
							className="col-1"
							style={{ padding: "15px", cursor: "pointer" }}
							onClick={handleCloseModal}>
							<img alt="close" src="../images/closeOne.png" />
						</div>
					</div>

					<div>
						{/* Render sections with content */}
						{nonEmptySections.map((section, sectionIndex) => (
							<div key={`section-${sectionIndex}-${section}`}>
								<p
									style={{
										fontWeight: 900,
										color: "#36BB7D",
										margin: "10px 0",
									}}>
									<input
										type="checkbox"
										value={section}
										checked={isSectionSelected(section)}
										onChange={handleEnableSectionCheckbox}
									/>
									<span style={{ marginLeft: "5px" }}>{section}</span>
								</p>

								{courses.map((course, courseIndex) =>
									course.textlist.map((item, textIndex) => {
										if (item.section === section) {
											return (
												<div
													key={`text-${courseIndex}-${textIndex}-${item.name}`}
													className="d-flex"
													style={{ marginLeft: "10px" }}>
													<input
														type="checkbox"
														value={item.name}
														checked={isItemSelected(item.name)}
														onChange={handleEnableCheckbox}
													/>
													<div style={{ marginLeft: "5px" }}>{item.name}</div>
													<div
														style={{
															cursor: "pointer",
															marginLeft: "15px",
															textDecoration: "underline",
														}}
														onClick={() => handleViewPdf(item.file)}>
														View
													</div>
												</div>
											);
										}
										return null;
									})
								)}
							</div>
						))}

						{/* Render items without sections */}
						{emptySections.length > 0 &&
							courses.map((course, courseIndex) =>
								course.textlist.map((item, textIndex) => {
									if (item.section === "" && item.file !== "") {
										return (
											<div
												key={`empty-section-${courseIndex}-${textIndex}-${item.name}`}
												className="d-flex align-items-end">
												<p
													style={{
														fontWeight: 900,
														color: "#36BB7D",
														margin: "10px 0",
													}}>
													<input
														type="checkbox"
														value={item.name}
														checked={isItemSelected(item.name)}
														onChange={handleEnableCheckbox}
													/>
													<span style={{ marginLeft: "5px" }}>{item.name}</span>
												</p>
												<div
													style={{
														cursor: "pointer",
														marginLeft: "15px",
														textDecoration: "underline",
													}}
													onClick={() => handleViewPdf(item.file)}>
													View
												</div>
											</div>
										);
									}
									return null;
								})
							)}
					</div>

					<div style={{ textAlign: "center", paddingBottom: "10px" }}>
						<Button
							style={{ background: "#20ce8f", border: "none" }}
							disabled={enablecheckeditems.length === 0}
							onClick={handlePushItemIntoTextlistArray}>
							Add Selected Files ({enablecheckeditems.length})
						</Button>
					</div>
				</Modal.Body>
			</Modal>

			<span
				onClick={() => setShowExistingFilesModel(true)}
				style={{ cursor: "pointer" }}>
				<img
					alt="icon"
					style={{
						width: "18px",
						height: "18px",
						background: "#20ce8f",
						borderRadius: "20px",
					}}
					src="../images/Icon.png"
				/>
				&nbsp;
				<span
					className="f-fm fm-w6-s16"
					style={{
						color: "#20ce8f",
						verticalAlign: "middle",
					}}>
					Add Existing File
				</span>
			</span>
		</>
	);
}
